import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useFormState } from "react-final-form";
import { Tooltip } from "@blueprintjs/core";
import { get, uniq } from "lodash";
import Input from "../Input";
import TextTruncate from "components/TextTruncate";
import { fromToValidator, isNotEmpty } from "utils/validate";
import * as styles from "./styles.module.scss";
const RangeValue = ({ from, to, canBeEqual, editable, className, units, customErrorMessage, errorInTooltip, suppressDash = false, }) => {
    const formState = useFormState();
    const { from: { error: fromError, active: fromActive, touched: fromTouched }, to: { error: toError, active: toActive, touched: toTouched }, } = useMemo(() => ({
        from: {
            error: get(formState.errors, from.name),
            active: formState.active === from.name,
            touched: get(formState.touched, from.name),
        },
        to: {
            error: get(formState.errors, to.name),
            active: formState.active === to.name,
            touched: get(formState.touched, to.name),
        },
    }), [formState.active, formState.errors, formState.touched, from.name, to.name]);
    const rangeErrors = useMemo(() => uniq([fromError, toError, from.externalError, to.externalError]).filter((i) => isNotEmpty(i)), [from.externalError, fromError, to.externalError, toError]);
    const customFromValidator = useCallback((value, state) => fromToValidator(value, get(state, to.name), canBeEqual ?? false, customErrorMessage), [to.name, canBeEqual, customErrorMessage]);
    const customToValidator = useCallback((value, state) => fromToValidator(get(state, from.name), value, canBeEqual ?? false, customErrorMessage), [from.name, canBeEqual, customErrorMessage]);
    const renderErrors = useMemo(() => {
        const errors = (React.createElement("div", { className: styles.errors }, rangeErrors.map((message, index) => (React.createElement("div", { key: index, className: styles["error-message"] }, message)))));
        if (!fromActive && !toActive && !!rangeErrors.length) {
            if (errorInTooltip) {
                return (React.createElement(Tooltip, { popoverClassName: styles["tooltip-portal"], isOpen: true, placement: "bottom", usePortal: false, content: errors, targetTagName: "div" },
                    React.createElement("div", { className: styles["errors-tooltipTarget"] })));
            }
            else {
                return errors;
            }
        }
    }, [errorInTooltip, fromActive, rangeErrors, toActive]);
    return (React.createElement("div", { className: classNames(styles["range-value"], className) },
        React.createElement("div", { className: styles.fields },
            React.createElement(Input, { ...from, readonly: !editable, customValidator: editable ? customFromValidator : undefined, showErrorIfUntouched: to.showErrorIfUntouched ?? toTouched, onUpdateValue: () => null, suppressErrors: true, required: true, isFormInput: true, suppressStateClass: toActive, classes: { ...from.classes, container: classNames(from.classes?.container, styles.inputContainer) } }),
            !suppressDash && (React.createElement("span", { className: classNames(styles["long-dash"], {
                    [styles["long-dash-noLabel"]]: !from.label && !to.label,
                }) })),
            React.createElement(Input, { ...to, readonly: !editable, customValidator: editable ? customToValidator : undefined, showErrorIfUntouched: from.showErrorIfUntouched ?? fromTouched, onUpdateValue: () => null, suppressErrors: true, required: true, isFormInput: true, suppressStateClass: fromActive, classes: { ...from.classes, container: classNames(from.classes?.container, styles.inputContainer) } })),
        isNotEmpty(units, true) && (React.createElement("div", { className: styles.units },
            React.createElement(TextTruncate, { text: units, className: styles.unit }),
            React.createElement(TextTruncate, { text: units, className: styles.unit }))),
        renderErrors));
};
export default RangeValue;
